import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { FORMS } from './data/forms';
import QuestionRenderer from './components/QuestionRenderer';
import ReviewScreen from './components/ReviewScreen';
import SignaturePad from './components/SignaturePad';
import { generatePDF } from './utils/generatePDF';

// Demo lockdown: when true, the app is a read-only showcase — nothing is
// persisted to localStorage and PDF saving is disabled. Flip to false to
// restore full functionality for a real client.
const DEMO_MODE = true;

function App() {
  const [screen, setScreen] = useState('intro'); // intro | form | review | done
  const [activeFormIdx, setActiveFormIdx] = useState(0);
  const [formStates, setFormStates] = useState(() => {
    if (DEMO_MODE) return FORMS.map(() => ({ questionIdx: 0, answers: {}, status: 'not-started', reviewing: false, pdfSaved: false }));
    try { return JSON.parse(localStorage.getItem('formStates')) || FORMS.map(() => ({ questionIdx: 0, answers: {}, status: 'not-started', reviewing: false, pdfSaved: false })); }
    catch { return FORMS.map(() => ({ questionIdx: 0, answers: {}, status: 'not-started', reviewing: false, pdfSaved: false })); }
  });
  const [globalName, setGlobalName] = useState(() => DEMO_MODE ? '' : (localStorage.getItem('globalName') || ''));
  const [globalSig, setGlobalSig] = useState(() => DEMO_MODE ? '' : (localStorage.getItem('globalSig') || ''));
  const [sigSaved, setSigSaved] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [segCollapsed, setSegCollapsed] = useState(true);
  const [showSegToggle, setShowSegToggle] = useState(false);
  const barRef = useRef(null);

  const activeForm = FORMS[activeFormIdx];
  const activeState = formStates[activeFormIdx];

  const updateState = (idx, patch) => {
    setFormStates(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  useEffect(() => {
    if (DEMO_MODE) return;
    localStorage.setItem('formStates', JSON.stringify(formStates));
    localStorage.setItem('globalName', globalName);
    localStorage.setItem('globalSig', globalSig);
  }, [formStates, globalName, globalSig]);

  useEffect(() => {
    if (screen === 'intro' && globalName && globalSig)
      window.scrollTo(0, document.body.scrollHeight);
    else
      window.scrollTo(0, 0);
  }, [screen, activeFormIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeState.questionIdx]);

  useEffect(() => {
    if (!globalName) return;
    const icIdx = FORMS.findIndex(f => f.id === 'informed-consent');
    if (icIdx < 0) return;
    setFormStates(prev => prev.map((s, i) =>
      i === icIdx
        ? { ...s, answers: { ...s.answers, ic_printed_name: globalName } }
        : s
    ));
  }, [globalName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (barRef.current) {
      const isTall = barRef.current.scrollHeight > 64;
      setShowSegToggle(isTall);
      setSegCollapsed(isTall);
    }
  }, [activeFormIdx, screen]);

  const visibleQuestions = activeForm.questions.filter(q => {
    if (q.showWithPrevious) return false;
    if (!q.showIf) return true;
    const ans = activeState.answers[q.showIf.id] || '';
    if (q.showIf.notEmpty) return !!ans;
    return ans === q.showIf.value || ans.split('||').includes(q.showIf.value);
  });

  const currentQ = visibleQuestions[activeState.questionIdx];
  const totalQ = visibleQuestions.length;
  const currentQFullIdx = currentQ ? activeForm.questions.findIndex(q => q.id === currentQ.id) : -1;
  const nextInFull = activeForm.questions[currentQFullIdx + 1];
  const pairedQ = nextInFull?.showWithPrevious ? nextInFull : null;
const setAnswer = (val, altId) => {
    const id = altId || currentQ.id;
    updateState(activeFormIdx, {
      answers: { ...activeState.answers, [id]: val },
      status: 'in-progress'
    });
  };

  const TB_BATCH_ITEMS = ['tb_active', 'tb_cough_3wk', 'tb_cough_blood', 'tb_exposed'];

  const goNext = () => {
    if (currentQ.id === 'tb_batch') {
      const yesItem = TB_BATCH_ITEMS.find(id => activeState.answers[id] === 'Yes');
      if (yesItem) {
        const label = currentQ.items.find(i => i.id === yesItem)?.label || yesItem;
        window.alert(`Because you answered Yes to "${label}", you should immediately contact your dentist. You may need to reschedule.`);
      }
    }
    if (activeState.questionIdx >= totalQ - 1) {
      updateState(activeFormIdx, { reviewing: true, status: 'complete' });
      setScreen('review');
    } else {
      updateState(activeFormIdx, { questionIdx: activeState.questionIdx + 1 });
    }
  };

  const goBack = () => {
    if (activeState.questionIdx === 0) {
      setScreen('intro');
    } else {
      updateState(activeFormIdx, { questionIdx: activeState.questionIdx - 1 });
    }
  };

  const startForm = (idx) => {
    setActiveFormIdx(idx);
    setScreen('form');
  };

  const switchTab = (idx) => {
    setActiveFormIdx(idx);
    const st = formStates[idx];
    if (st.reviewing || st.status === 'complete') setScreen('review');
    else setScreen('form');
  };

  const goToQuestion = (idx) => {
    updateState(activeFormIdx, { questionIdx: idx, reviewing: false });
    setScreen('form');
  };

  const editQuestion = (qId) => {
    const idx = visibleQuestions.findIndex(q => q.id === qId);
    if (idx >= 0) {
      updateState(activeFormIdx, { questionIdx: idx, reviewing: false });
      setScreen('form');
    }
  };

  const handleSavePDF = async () => {
    if (DEMO_MODE) {
      window.alert('This is a demo — saving is disabled.');
      return;
    }
    const effectiveAnswers = { ...activeState.answers };
    activeForm.questions.forEach(q => {
      if (q.type === 'signature' && !effectiveAnswers[q.id] && globalSig)
        effectiveAnswers[q.id] = globalSig;
    });
    await generatePDF(activeForm, effectiveAnswers, globalName);
    updateState(activeFormIdx, { pdfSaved: true });
    const allSaved = formStates.every((s, i) => i === activeFormIdx ? true : s.pdfSaved);
    if (allSaved) setTimeout(() => setScreen('intro'), 800);
  };

  const getTabStatus = (idx) => {
    const s = formStates[idx];
    if (allDone) return 'complete';
    if (s.status === 'complete' || s.reviewing) return 'in-progress';
    if (s.status === 'in-progress') return 'in-progress';
    return 'not-started';
  };
  const statusIcon = (st) => st === 'complete' ? '✓' : st === 'in-progress' ? '●' : '○';

  const allDone = formStates.every(s => s.pdfSaved);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {DEMO_MODE && (
        <div className="demo-banner">
          <strong>Demonstration only.</strong> Nothing you type is saved or sent.
        </div>
      )}
      <div className="app-header">
        <div className="logo">🦷</div>
        <h1>Dental Intake Forms</h1>
        {/* An invented practice, checked against all 1,102 rows of the dental sweep so it
            collides with no real one. The demo goes to prospects, and "[Practice Name]"
            read as unfinished rather than as a placeholder (James, 2026-10-23). */}
        <span className="subtitle">Harbor Lane Dental</span>
      </div>

      {screen !== 'intro' && (
        <div className="form-tabs">
          {FORMS.map((f, i) => {
            const st = getTabStatus(i);
            return (
              <button key={f.id} className={`form-tab ${i === activeFormIdx ? 'active' : ''}`} onClick={() => switchTab(i)}>
                <span className={`tab-status ${st}`}>{statusIcon(st)}</span>
                {f.shortTitle}
              </button>
            );
          })}
        </div>
      )}

      <div className="main-content">

        {screen === 'intro' && (
          <div className="intro-screen">
            <div className="welcome-badge">Patient Intake</div>
            <h2>Welcome to Your Dental Forms</h2>
            <div className="intro-instructions">
              <h3>Instructions</h3>
              <p>This packet contains three standard forms required for your appointment. You may complete the forms in any order. When finished with a form you can review your responses. After reviewing, save and download a pdf of the form.</p>
              <p><strong>iPhone users:</strong> When saving a form, the PDF will open in your browser. Tap the share icon and select "Save to Files" to save it to your device.</p>
            </div>
            <div className="sig-setup-card">
              <h3>Setup Your Printed Name and Signature</h3>
              <p>They can be used in the forms you will be completing.</p>
              <div className="input-label">Printed Name</div>
              <input
                className="text-input"
                placeholder="Type your full name"
                value={globalName}
                onChange={e => setGlobalName(e.target.value)}
              />
              <div className="input-label" style={{ marginTop: '24px' }}>Draw Signature {sigSaved && <span className="saved-badge">✓ Saved</span>}</div>
              <SignaturePad value={globalSig} onChange={v => { setGlobalSig(v); setSigSaved(false); }} onSave={() => setSigSaved(true)} hideTabs />
            </div>
            <div className="forms-grid">
              {FORMS.map((f, i) => {
                const st = getTabStatus(i);
                return (
                  <div className="form-card" key={f.id} onClick={() => startForm(i)}>
                    <h3>{f.title}</h3>
                    <p>{f.description}</p>
                    <span className={`card-status ${st}`}>
                      {st === 'not-started' ? 'Not Started' : st === 'in-progress' ? 'In Progress' : '✓ Complete'}
                    </span>
                  </div>
                );
              })}
            </div>
            {allDone && (
              <div style={{ textAlign: 'center', marginTop: '24px', padding: '16px', color: 'var(--green)', fontWeight: 700, fontSize: '20px' }}>
                🎉 All Forms Complete!
                <div style={{ fontSize: '20px', fontWeight: 400, marginTop: '6px', color: 'var(--green)' }}>Make sure each form is saved before starting over.</div>
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: '16px', marginBottom: '8px' }}>
              {confirmClear ? (
                <div className="confirm-clear-box">
                  <p className="confirm-clear-msg">Are you sure you want to clear all data? This cannot be reversed!</p>
                  <div className="confirm-clear-btns">
                    <button className="btn-back" onClick={() => {
                      localStorage.clear();
                      setFormStates(FORMS.map(() => ({ questionIdx: 0, answers: {}, status: 'not-started', reviewing: false, pdfSaved: false })));
                      setGlobalName('');
                      setGlobalSig('');
                      setSigSaved(false);
                      setActiveFormIdx(0);
                      setConfirmClear(false);
                    }}>Yes, Clear Everything</button>
                    <button className="btn-next" onClick={() => setConfirmClear(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button className="btn-back" style={{ borderRadius: '12px', padding: '16px 32px' }} onClick={() => setConfirmClear(true)}>
                  Clear Form and Start New Patient
                </button>
              )}
            </div>

          </div>
        )}

        {screen === 'form' && currentQ && (
          <div className="question-screen">
            <div className="progress-header">
              <span className="progress-label">
                <strong>{activeForm.shortTitle}</strong> &nbsp;·&nbsp; Page {activeState.questionIdx + 1} of {totalQ}
              </span>
              <button className="btn-back" style={{ padding: '8px 16px', fontSize: '20px', minWidth: 'auto' }} onClick={() => setScreen('intro')}>
                Back to Main Screen
              </button>
            </div>
            <div ref={barRef} className={`segmented-bar${segCollapsed ? ' collapsed' : ''}${showSegToggle ? ' has-toggle' : ''}`}>
              {visibleQuestions.map((q, idx) => (
                <button
                  key={q.id}
                  className={`seg-item${(q.type === 'yndk-batch' ? q.items.some(item => !!activeState.answers[item.id]) : q.type === 'multi-text' ? q.fields.some(f => !!activeState.answers[f.id]) : !!activeState.answers[q.id]) ? ' answered' : ''}${idx === activeState.questionIdx ? ' current' : ''}`}
                  onClick={() => goToQuestion(idx)}
                  aria-label={`Go to page ${idx + 1}`}
                />
              ))}
            </div>
            {showSegToggle && (
              <button className="seg-toggle" onClick={() => setSegCollapsed(c => !c)}>
                {segCollapsed ? '▼ Expand' : '▲ Collapse'}
              </button>
            )}

            <div className="question-card">
              <div className="question-section-label">{currentQ.section}</div>
              {(currentQ.label || currentQ.title) && <div className="question-text">{currentQ.label || currentQ.title}</div>}
              <QuestionRenderer
                question={currentQ}
                value={activeState.answers[currentQ.id]}
                onChange={(val, altId) => setAnswer(val, altId)}
                onSetAll={(answersMap) => updateState(activeFormIdx, { answers: { ...activeState.answers, ...answersMap }, status: 'in-progress' })}
                allAnswers={activeState.answers}
                onNext={goNext}
                globalSig={globalSig}
                globalName={globalName}
              />
              {pairedQ && (
                <div style={{ marginTop: '28px' }}>
                  {!pairedQ.hidePairedLabel && <div className="question-text" style={{ fontSize: '20px', marginBottom: '12px' }}>{pairedQ.label}</div>}
                  <QuestionRenderer
                    question={pairedQ}
                    value={activeState.answers[pairedQ.id]}
                    onChange={(val, altId) => setAnswer(val, altId || pairedQ.id)}
                    allAnswers={activeState.answers}
                    onNext={goNext}
                    globalSig={globalSig}
                    globalName={globalName}
                  />
                </div>
              )}
            </div>

            <div className="nav-buttons">
              <button className="btn-back" onClick={goBack}>← Back</button>
              <button className="btn-next" onClick={goNext}>
                {activeState.questionIdx >= totalQ - 1 ? 'Review All Answers →' : 'Next →'}
              </button>
            </div>
            {activeState.questionIdx < totalQ - 1 && (
              <div style={{ textAlign: 'center', marginTop: '32px' }}>
                <button className="btn-back" onClick={() => goToQuestion(totalQ - 1)}>Skip to Last Question</button>
              </div>
            )}
          </div>
        )}

        {screen === 'review' && (
          <ReviewScreen
            form={activeForm}
            answers={activeState.answers}
            onEdit={editQuestion}
            onSavePDF={handleSavePDF}
            pdfSaved={activeState.pdfSaved}
            onGoHome={() => setScreen('intro')}
            demoMode={DEMO_MODE}
          />
        )}


      </div>
      <footer className="app-footer">
        © {new Date().getFullYear()} Corcoran Software Solutions, L.L.C. All rights reserved.
      </footer>
    </div>
  );
}

export default App;
